import { useReducer } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { TodoContextLine } from "./TodoContextLine";
import { Activity, UpdateCustomerActivityInput, useGetTodoTranscript, useUpdateActivity } from "@/lib/api/customer";

export function useTodoTranscript(todoId: string, enabled: boolean) {
  const transcriptQuery = useGetTodoTranscript(todoId, enabled);
  const transcriptResult = transcriptQuery.data;
  const transcript = transcriptResult?.success
    ? transcriptResult.data.transcript
    : null;
  const transcriptError = transcriptQuery.isError
    ? "An unexpected error occurred while loading the transcript."
    : transcriptResult && !transcriptResult.success
      ? transcriptResult.message || "The transcript could not be loaded."
      : null;

  return { transcriptQuery, transcript, transcriptError };
}

export function TodoTranscriptContent({
  transcriptQuery,
  transcript,
  transcriptError,
}: ReturnType<typeof useTodoTranscript>) {
  if (transcriptQuery.isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <span className="text-xs text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (transcriptError) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <p>{transcriptError}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => transcriptQuery.refetch()}
          disabled={transcriptQuery.isFetching}
        >
          {transcriptQuery.isFetching ? "Retrying..." : "Try Again"}
        </Button>
      </div>
    );
  }

  if (transcript === null) return null;

  if (!transcript.trim()) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-md bg-muted/20 p-4 text-sm text-muted-foreground">
        This transcript is empty.
      </div>
    );
  }

  return (
    <ScrollArea className="min-h-0 flex-1 rounded-md bg-muted/20 p-4">
      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
        {transcript}
      </pre>
    </ScrollArea>
  );
}

interface TodoTranscriptDialogProps {
  todo: Activity | null;
  onClose: () => void;
}

interface DialogState {
  // Local copy of the saved text so Save/Cancel stay correct until the list
  // refetches.
  savedContent: string;
  draft: string;
  isSaving: boolean;
  isDiscardDialogOpen: boolean;
}

type DialogAction =
  | { type: "draftChanged"; draft: string }
  | { type: "draftReverted" }
  | { type: "saveStarted" }
  | { type: "saveFinished" }
  | { type: "contentSaved" }
  | { type: "discardPromptToggled"; isOpen: boolean }
  | { type: "changesDiscarded" };

function initialStateOf(todo: Activity | null): DialogState {
  const content = todo?.content ?? "";
  return {
    savedContent: content,
    draft: content,
    isSaving: false,
    isDiscardDialogOpen: false,
  };
}

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "draftChanged":
      return { ...state, draft: action.draft };
    case "draftReverted":
      return { ...state, draft: state.savedContent };
    case "saveStarted":
      return { ...state, isSaving: true };
    case "saveFinished":
      return { ...state, isSaving: false };
    case "contentSaved":
      return { ...state, savedContent: state.draft };
    case "discardPromptToggled":
      return { ...state, isDiscardDialogOpen: action.isOpen };
    case "changesDiscarded":
      return {
        ...state,
        draft: state.savedContent,
        isDiscardDialogOpen: false,
      };
  }
}

// Keying on the todo id remounts the dialog, which resets its state when a
// different todo is opened.
export function TodoTranscriptDialog({ todo, onClose }: TodoTranscriptDialogProps) {
  return (
    <TodoTranscriptDialogContent
      key={todo?.id ?? "closed"}
      todo={todo}
      onClose={onClose}
    />
  );
}

function TodoTranscriptDialogContent({ todo, onClose }: TodoTranscriptDialogProps) {
  const { toast } = useToast();
  const updateActivity = useUpdateActivity();
  const isOpen = todo !== null;
  const transcriptState = useTodoTranscript(todo?.id ?? "", isOpen);

  const [state, dispatch] = useReducer(dialogReducer, todo, initialStateOf);
  const { savedContent, draft, isSaving, isDiscardDialogOpen } = state;

  const isEditable = !!todo && !todo.approved_date && !todo.hidden_date;
  const hasUnsavedChanges = draft !== savedContent;
  const canSave = isEditable && hasUnsavedChanges && draft.trim().length > 0 && !isSaving;

  const closeWithoutConfirmation = () => {
    dispatch({ type: "changesDiscarded" });
    onClose();
  };

  const requestClose = () => {
    if (isSaving) return;

    if (hasUnsavedChanges) {
      dispatch({ type: "discardPromptToggled", isOpen: true });
      return;
    }

    closeWithoutConfirmation();
  };

  const handleSave = async () => {
    if (!todo || !canSave) return;

    dispatch({ type: "saveStarted" });

    try {
      // This route rewrites status and assignment from the request, so send the
      // todo's current values to leave them as they are.
      const input = UpdateCustomerActivityInput.safeParse({
        id: todo.id,
        resource_type: "TODO",
        customer_id: todo.customer_id,
        content: draft,
        status: todo.status || "TODO",
        assigned_to_id: todo.last_assigned_to_id || undefined,
      });

      if (!input.success) {
        toast({ title: "Unable to save Todo", description: "The Todo text is invalid.", variant: "destructive" });
        return;
      }

      const result = await updateActivity(input.data);

      if (!result.success) {
        toast({
          title: "Unable to save Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      dispatch({ type: "contentSaved" });
      toast({ title: "Todo updated", description: "Your changes were saved." });
    } catch {
      toast({
        title: "Unable to save Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "saveFinished" });
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) requestClose();
        }}
      >
        <DialogContent className="flex h-[calc(100vh-5vw)] w-[95vw] max-w-[95vw] flex-col sm:h-[calc(100vh-10vw)] sm:max-w-[90vw]">
          <DialogHeader>
            <DialogTitle>Call Transcript</DialogTitle>
            <TodoContextLine todo={todo} />
            <DialogDescription>
              Review the call this Todo was created from. The Todo can be edited
              only while it needs review.
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:flex-row md:overflow-visible">
            <div className="flex min-h-[16rem] min-w-0 flex-1 flex-col">
              <TodoTranscriptContent {...transcriptState} />
            </div>
            <div className="flex min-h-[16rem] flex-col gap-2 md:w-2/5 md:max-w-xl md:shrink-0">
              <label htmlFor="todo-edit-content" className="text-sm font-medium">
                Todo
              </label>
              <Textarea
                id="todo-edit-content"
                data-testid="todo-edit-content"
                value={draft}
                onChange={(event) =>
                  dispatch({ type: "draftChanged", draft: event.target.value })
                }
                className="min-h-0 flex-1 resize-none"
                readOnly={!isEditable}
                disabled={isSaving}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="todo-edit-cancel"
                  onClick={() => dispatch({ type: "draftReverted" })}
                  disabled={!isEditable || !hasUnsavedChanges || isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  data-testid="todo-edit-save"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
              {!isEditable && (
                <p className="text-xs text-muted-foreground">
                  Approved or hidden Todos cannot be edited.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
              disabled={isSaving}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDiscardDialogOpen}
        onOpenChange={(isOpen) =>
          dispatch({ type: "discardPromptToggled", isOpen })
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Discard unsaved changes?
            </DialogTitle>
            <DialogDescription>
              Your edits to this Todo have not been saved. Discarding them
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                dispatch({ type: "discardPromptToggled", isOpen: false })
              }
            >
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={closeWithoutConfirmation}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
